import React, { ReactNode } from "react";
import { styled, useTheme } from "@superset-ui/core";
import { Popover } from "antd";

import ControlHeader from "src/explore/components/ControlHeader";

import PopoverContent from "./Content";

type ValidationError = string;
export interface LegendConfigControlProps {
    configList: string[];
    onChange: (params: { name: string; value: string }[] | undefined) => void;
    value?: { name: string; value: string }[];
    actions: any;
    formData?: any;
    name?: string;
    label?: ReactNode;
    description?: ReactNode;
    validationErrors?: ValidationError[];
    renderTrigger?: boolean;
    warning?: string;
    danger?: string;
    hovered?: boolean;
    onClick?: () => void;
    tooltipOnClick?: () => void;
    rightNode?: ReactNode;
    leftNode?: ReactNode;
}

// eslint-disable-next-line theme-colors/no-literal-colors
const StyledList = styled.div<{ color: string; hoverColor: string; hoverBg: string }>`
    padding: 4px 9px;
    height: 32px;
    display: flex;
    align-items: center;
    cursor: pointer;
    border-bottom: 1px solid ${(props) => props.color};
    position: relative;
    &:hover {
        background-color: ${(props) => props.hoverBg};
    }
    &:last-child {
        border-bottom: "none";
    }
    .fa {
        color: 1px solid ${(props) => props.color};
    }
`;

const StyledListContainer = styled.div<{ color: string }>`
    border: 1px solid ${(props) => props.color};
    border-radius: 4px;
`;

const LegendConfigControl = (props: LegendConfigControlProps) => {
    const { actions, formData, configList } = props;

    const { colors, gridUnit } = useTheme();

    const legendConfig = React.useMemo(() => {
        let newValue: { name: string; value: string }[] = [];
        if (formData.legend_config) {
            const validConfig = formData.legend_config.filter((cog: { name: string }) => configList.includes(cog.name));
            // DESC : 기존값이 있지만 새로운 리스트와 일치하는 것이 하나도 없으면 전부 value를 reset
            newValue = !validConfig.length ? configList.map((list) => ({ name: list, value: "" })) : validConfig;
        } else {
            newValue = configList.map((list) => ({ name: list, value: "" }));
        }
        return newValue;
    }, [formData, configList]);

    const setLegendConfig = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        if (legendConfig) {
            const newLabelConfig = [...legendConfig].map((config) => {
                if (config.name === name) {
                    return { name, value };
                }
                return config;
            });
            actions.setControlValue("legend_config", newLabelConfig);
        }
    };

    return (
        <div>
            <ControlHeader
                name={props.name}
                label={props.label}
                description={props.description}
                validationErrors={props.validationErrors}
                renderTrigger={props.renderTrigger}
                rightNode={props.rightNode}
                leftNode={props.leftNode}
                onClick={props.onClick}
                hovered={props.hovered}
                tooltipOnClick={props.tooltipOnClick}
                danger={props.danger}
                warning={props.warning}
            />
            <StyledListContainer color={colors.grayscale.light2}>
                {legendConfig.map((list, index) => (
                    <Popover
                        key={`popover_${index}_${list.name}`}
                        title={list.name}
                        trigger="click"
                        placement="right"
                        content={() => (
                            <PopoverContent label={list.name} value={list.value} onChange={setLegendConfig} />
                        )}
                    >
                        <StyledList
                            hoverColor={colors.grayscale.light4}
                            hoverBg={colors.grayscale.light4}
                            color={colors.grayscale.light2}
                            key={`index_${index}`}
                        >
                            {list.name}
                            <i
                                className="fa fa-caret-right"
                                style={{
                                    position: "absolute",
                                    right: 3 * gridUnit,
                                    top: 3 * gridUnit,
                                }}
                            />
                        </StyledList>
                    </Popover>
                ))}
            </StyledListContainer>
        </div>
    );
};

export default React.memo(LegendConfigControl);
